import {
    adjustLabels,
    wordWrapTextNode,
    formatValue,
    createGradient,
    textWidth,
    MARGINS,
} from "./forecast-plots-plotting-utils.js";

/**
 * Creates the title block of the water supply forecasts plot
 * using the provided 'chart' and the selected options
 * in the 'inputs' object. The title block is
 * appended to the SVG as it's own svg <g> element.
 *
 * @param {node} chart - d3 svg object
 * @param {Object} inputs - selected options including pubdate and basin name.
 */
export function createTitleBlock(chart, inputs) {
    // New group <g> object to contain the title
    const titleGroup = chart.append("g");
    titleGroup.attr("id", "title-group");
    titleGroup.attr("transform", `translate(${+inputs.chartWidth / 2}, ${MARGINS.top})`);
    titleGroup.attr("text-anchor", "middle");
    titleGroup.attr("font-weight", "bold");
    titleGroup.attr("font-size", "12px");

    // Black border and filled rectangle around title block
    titleGroup
        .append("rect")
        .attr("x", -1 * (+inputs.chartWidth / 2))
        .attr("width", +inputs.chartWidth)
        .attr("y", -MARGINS.top)
        .attr("height", 66)
        .attr("fill", "#f5f5f5")
        .attr("stroke", "black")
        .attr("stroke-width", 1);

    // Text (basin, pub-date, forecast status)
    titleGroup
        .append("text")
        .attr("font-size", "14px")
        .attr("y", 20 - MARGINS.top)
        .text(inputs.basin);

    let txt = "Water Supply Forecasts";
    if (inputs.forecastStatus === "prelim") {
        txt = "Preliminary " + txt;
    }
    if (inputs.forecastStatus === "raw") {
        txt = "Raw " + txt;
    }
    if (inputs.forecastStatus === "guidance") {
        txt = "Guidance " + txt;
    }
    titleGroup
        .append("text")
        .attr("y", 20 + 1 * 18 - MARGINS.top)
        .text(txt);

    titleGroup
        .append("text")
        .attr("y", 20 + 2 * 18 - MARGINS.top)
        .text(inputs.pubDateText);
}

/**
 * Creates the row above the chart entries  that has the
 * header labels such as 'forecast point', 'forecast period', etc.
 *
 * @param {node} chart - d3 svg object
 * @param {Object} inputs - selected options including
 *      chartWidth, normalType, labelUnits.
 */
export function createChartTableHeaderRow(chart, inputs) {
    // Get the width of the data-section of the chart
    const width = +inputs.chartWidth - MARGINS.left - MARGINS.right;

    // Create the header row's <g> element
    const chartHeader = chart.append("g");
    chartHeader.attr("id", "header-group");
    chartHeader.attr("transform", `translate(180, ${MARGINS.top})`);
    chartHeader.attr("font-size", "12px");

    // Forecast Point and Forecast Period labels
    chartHeader
        .append("text")
        .attr("x", -158)
        .attr("y", 90 + 1 * 18 - MARGINS.top)
        .text("Forecast");
    chartHeader
        .append("text")
        .attr("x", -158)
        .attr("y", 90 + 2 * 18 - MARGINS.top)
        .text("Point");
    chartHeader
        .append("text")
        .attr("x", -58)
        .attr("y", 90 + 1 * 18 - MARGINS.top)
        .text("Forecast");
    chartHeader
        .append("text")
        .attr("x", -58)
        .attr("y", 90 + 2 * 18 - MARGINS.top)
        .text("Period");

    // Forecast exceedance explainer label
    chartHeader
        .append("text")
        .attr("text-anchor", "middle")
        .attr("x", width / 2)
        .attr("y", 90 + 0 * 18 - MARGINS.top)
        .text("Forecast Exceedance Probabilities");
    chartHeader
        .append("text")
        .attr("font-size", "11px")
        .attr("text-anchor", "middle")
        .attr("x", width / 2)
        .attr("y", 90 + 1 * 18 - MARGINS.top)
        .text("   < - - - - - -  Drier  - - - - - - -    Future Conditions    - - - - - - -  Wetter  - - - - - - >")
        .attr("xml:space", "preserve");
    let bottomText = "Labels on chart represent volumes of water expressed as percent of median (1991-2020 period).";
    if (inputs.labelUnit !== "PCT") {
        bottomText = "Labels on chart represent volumes of water expressed in thousand acre-feet.";
    } else if (inputs.normalType === "AVG") {
        bottomText = "Labels on chart represent volumes of water expressed as percent of average (1991-2020 period).";
    }
    chartHeader
        .append("text")
        .attr("font-size", "11px")
        .attr("text-anchor", "middle")
        .attr("x", width / 2)
        .attr("y", 90 + 2 * 18 - MARGINS.top)
        .text(bottomText);

    // Divider line between header and data.
    chartHeader
        .append("line")
        .attr("x1", -MARGINS.left + 21)
        .attr("x2", width + 1)
        .attr("y1", 0 - 0.5)
        .attr("y2", 0 - 0.5)
        .attr("stroke", "black")
        .attr("stroke-width", 1);
}

/**
 * Creates a new row in the forecast plot for the given data entry,
 * plotting (as necessary) the forecast, the observation, the normal,
 * the min, the max, and any associated labels.
 *
 * @param {*} chart - d3 chart.
 * @param {*} entry - data entry
 * @param {*} inputs - specified chart options
 * @param {*} scale - d3js x-axis scale method
 */
export function createChartEntry(chart, entry, inputs, scale) {
    const spacing = {
        betweenStations: 26,
        betweenPeriods: 62,
    };

    const width = +inputs.chartWidth - MARGINS.left - MARGINS.right;

    // get the entry group
    const entryGroup = chart.select("#entry-group");

    // Get the current y-position and last charted forecast point.
    let currentY = +chart.attr("data-current-y");
    const lastStation = chart.attr("data-last-station");

    // If we're plotting a new forecast point, draw the name
    if (entry.id !== lastStation) {
        // Update the current y position
        currentY += spacing.betweenStations;

        // Add a text node
        const nameNode = entryGroup
            .append("text")
            .attr("x", -158)
            .attr("y", currentY + 20)
            .attr("text-anchor", "start")
            .text(null);

        // Word wrap the forecast name
        if (entry.name.split(/\s+/).length > 1) {
            wordWrapTextNode(nameNode, entry.name);
        }

        // set the new station as the last charted station
        chart.attr("data-last-station", entry.id);
    }

    // draw the period text
    entryGroup
        .append("text")
        .attr("x", -58)
        .attr("y", currentY + 20)
        .attr("text-anchor", "start")
        .text(entry.period);

    // draw the number of observations  (if needed)
    if (inputs.showNumberObservations) {
        entryGroup
            .append("text")
            .attr("font-style", "italic")
            .attr("fill", "#777")
            .attr("x", -52)
            .attr("y", currentY + 36)
            .text(entry.numberObservations + " obs.");
    }

    // horizontal line across the chart area
    entryGroup
        .append("line")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", currentY + 15.5)
        .attr("y2", currentY + 15.5)
        .attr("stroke", "black")
        .attr("stroke-width", 0.5);

    // draw the forecast (if needed)
    if (inputs.showForecast && entry.hasForecastData) {
        createForecast(entryGroup, currentY, entry, inputs, scale);
    }

    // draw the observation (if needed)
    if (inputs.showObserved && entry.obsPctOfNormal !== null) {
        let label = null;
        if (inputs.showObservedLabel) {
            label = inputs.labelUnit === "VOL" ? formatValue(entry.observed) : Math.round(entry.obsPctOfNormal) + "%";
        }
        createOtherTickLabel(entryGroup, currentY, scale(entry.obsPctOfNormal) - 1, label, "black");
    }

    // draw the POR minimum (if needed)
    if (inputs.showMin && entry.minPctOfNormal !== null) {
        let label = null;
        if (inputs.showMinLabel || inputs.showMinYear) {
            label = "";
            if (inputs.showMinLabel) {
                label += inputs.labelUnit === "VOL" ? formatValue(entry.min) : Math.round(entry.minPctOfNormal) + "%";
            }
            if (inputs.showMinYear) {
                label += ` (${entry.minYear})`;
            }
        }
        createOtherTickLabel(entryGroup, currentY, scale(entry.minPctOfNormal) - 1, label, "red");
    }

    // draw the normal (median or average) (if needed)
    if (inputs.showNormal && entry.normalPctOfNormal !== null) {
        let label = null;
        if (inputs.showNormalLabel) {
            label = inputs.labelUnit === "VOL" ? formatValue(entry.normal) : "100%";
        }
        createOtherTickLabel(entryGroup, currentY, scale(100) - 1, label, "green");
    }

    // draw the POR maximum (if needed)
    if (inputs.showMax && entry.maxPctOfNormal !== null) {
        let label = null;
        if (inputs.showMaxLabel || inputs.showMaxYear) {
            label = "";
            if (inputs.showMaxLabel) {
                label += inputs.labelUnit === "VOL" ? formatValue(entry.max) : Math.round(entry.maxPctOfNormal) + "%";
            }
            if (inputs.showMaxYear) {
                label += ` (${entry.maxYear})`;
            }
        }
        createOtherTickLabel(entryGroup, currentY, scale(entry.maxPctOfNormal) - 1, label, "blue");
    }

    // Re-position the other tick labels to avoid overlaps
    // and crossing the zero-boundary.
    let textNodes = Array.from(document.querySelectorAll(`[data-line-id="${Math.round(currentY).toString()}"]`));
    adjustLabels(textNodes);

    // increment and set the current y value
    currentY += spacing.betweenPeriods;
    chart.attr("data-current-y", currentY.toString());
}

/**
 *
 * Creates the forecast-bar, and the forecast labels for the
 * specified data entry.
 *
 * @param {*} group - the entry-group node
 * @param {*} currentY - current y position of the chart
 * @param {*} entry - entry (see createEntries)
 * @param {*} inputs - selected graph options
 * @param {*} scale - the d3 scale method for the x-axis
 * @returns
 */
function createForecast(group, currentY, entry, inputs, scale) {
    // figure out the start and end sections of the bar.
    let barStart = scale(entry.forecastValuesPctOfNormal[0]);
    let barEnd = scale(entry.forecastValuesPctOfNormal[4]);

    // Check for negative bar
    let reverse = false;
    if (barEnd < barStart) {
        [barStart, barEnd] = [barEnd, barStart];
        reverse = true;
    }

    // dont draw infinite bars.
    if (!isFinite(barStart) || !isFinite(barEnd)) {
        return;
    }

    // Create the linear gradient
    const gradientID = "gradient" + Math.ceil(currentY).toString();
    const offsets = entry.forecastValuesPctOfNormal.slice(1, 4).map((v) => {
        const x = scale(v);
        const pct = Math.round((100 * (x - barStart)) / (barEnd - barStart));
        return pct;
    });
    const gradient = createGradient(gradientID, offsets, reverse);
    document.querySelectorAll("defs")[0].appendChild(gradient);

    // constrain bar to positive space
    barStart = Math.max(barStart, 0);

    // draw the bar
    group
        .append("rect")
        .attr("x", barStart)
        .attr("y", currentY + 11)
        .attr("width", barEnd - barStart)
        .attr("height", 10)
        .attr("fill", `url(#${gradientID})`);

    // Draw the ticks
    entry.forecastValuesPctOfNormal.map((v) => {
        if (v !== null) {
            if (inputs.hideNeg && v < 0) {
                return;
            } else {
                group
                    .append("rect")
                    .attr("x", scale(v) - 1)
                    .attr("y", currentY + 11)
                    .attr("width", 2)
                    .attr("height", 16)
                    .attr("stroke", "none")
                    .attr("fill", "#000");
            }
        }
    });

    // draw the labels if needed
    if (inputs.showForecastLabel) {
        createForecastLabels(group, currentY, entry, scale, inputs);
    }
}

/**
 * Draws a tick on the forecast chart at the specified x and y position
 * using the specified label (label isn't drawn if it's null), and the
 * specified color.
 *
 * @param {*} group - the entry-group node
 * @param {*} currentY - current y position of the chart
 * @param {*} xPos - the scaled x-position for the tick/label
 * @param {*} label - the label to be associated (no label is drawn
 *                  if this is null)
 * @param {*} color - the color to use.
 */
function createOtherTickLabel(group, currentY, xPos, label, color) {
    group
        .append("rect")
        .attr("x", xPos)
        .attr("y", currentY + 6.5)
        .attr("rx", 4)
        .attr("ry", 4)
        .attr("width", 4)
        .attr("height", 22)
        .attr("stroke", color)
        .attr("stroke-width", 2)
        .attr("fill", color)
        .attr("fill-opacity", 0.3);
    if (label) {
        group
            .append("text")
            .attr("x", xPos)
            .attr("y", currentY + 40.5)
            .attr("font-style", "italic")
            .attr("text-anchor", "middle")
            .attr("fill", color)
            .attr("data-line-id", Math.round(currentY).toString())
            .text(label);
    }
}

/**
 * Creates the text elements for each forecast label that
 * is needed. this function then adjusts the locations of the
 * the text elements so that none of them overlap.
 *
 * @param {*} group - the entry-group node
 * @param {*} currentY - current y position of the chart
 * @param {*} entry - entry (see createEntries)
 * @param {*} inputs - selected graph options
 * @param {*} scale - the d3 scale method for the x-axis
 */
function createForecastLabels(group, currentY, entry, scale, inputs) {
    // First, find the x-locations and widths corresponding to each label
    const xLocs = [];
    const exceedences = [];
    const labels = [];
    const labelWidths = [];
    if (entry.forecastValuesPctOfNormal[1] < entry.forecastValuesPctOfNormal[0]) {
        entry.forecastValuesPctOfNormal = entry.forecastValuesPctOfNormal.reverse();
    }
    entry.forecastValuesPctOfNormal.forEach((pct, idx) => {
        // x-location
        xLocs.push(scale(pct));

        // exceedence
        exceedences.push(entry.forecastProbabilities[idx]);

        // labelname and width
        if (inputs.labelUnit === "VOL") {
            const label = formatValue(entry.forecastValues[idx]);
            labels.push(label);
            labelWidths.push(textWidth(label));
        } else {
            const label = pct === null ? "" : Math.round(pct) + "%";
            labels.push(label);
            labelWidths.push(textWidth(label));
        }
    });

    // Starting in the middle, adjust the label locations
    // to avoid overlaps with other labels
    if (xLocs[2] + labelWidths[2] + 6 >= xLocs[3]) {
        xLocs[3] += xLocs[2] + labelWidths[2] + 6 - xLocs[3];
    }
    if (xLocs[3] + labelWidths[3] + 6 >= xLocs[4]) {
        xLocs[4] += xLocs[3] + labelWidths[3] + 6 - xLocs[4];
    }
    if (xLocs[1] + labelWidths[1] + 6 >= xLocs[2]) {
        xLocs[1] -= xLocs[1] + labelWidths[1] + 6 - xLocs[2];
    }
    if (xLocs[0] + labelWidths[0] + 6 >= xLocs[1]) {
        xLocs[0] -= xLocs[0] + labelWidths[0] + 6 - xLocs[1];
    }

    // Next check that no labels start below the miminimum chart
    // value on the x-axis. push all labels forward if we need to.
    if (xLocs[0] - 6 - labelWidths[0] / 2 < 0) {
        xLocs[0] -= xLocs[0] - 6 - labelWidths[0] / 2;
        for (let j = 0; j < xLocs.length - 1; j++) {
            if (xLocs[j] + labelWidths[j] / 2 + 6 + labelWidths[j + 1] / 2 >= xLocs[j + 1]) {
                xLocs[j + 1] += xLocs[j] + labelWidths[j] / 2 + 6 + labelWidths[j + 1] / 2 - xLocs[j + 1];
            }
        }
    }

    // draw the labels
    const visible = inputs.forecastLabels === "ALL";
    labels.forEach((label, idx) => {
        group
            .append("text")
            .attr("font-size", "12px")
            .attr("text-anchor", "middle")
            .attr("x", xLocs[idx])
            .attr("y", currentY + 3)
            .attr("visibility", visible || idx === 2 ? "visible" : "hidden")
            .attr("font-weight", idx === 2 ? "bold" : "normal")
            .attr("text-decoration", [95, 5].includes(exceedences[idx]) ? "underline" : "none")
            .text(label);
    });
}

/**
 * Appends the legend to the bottom of the chart. The legend is a
 * static SVG image and only needs to know where the middle of the
 * chart is (which it can learn from the chart's width attribute)
 *
 * @param {node} chart - d3 svg object
 */
export function createLegendGroup(chart) {
    const width = +chart.attr("width");
    const halfWidth = width / 2;

    const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
    g.id = "legend-group";

    // Legend Background
    g.innerHTML += /*html*/ `<line x1="${-halfWidth}" x2="${width}" y1="0" y2="0" stroke="black" stroke-width="1"></line>`;
    g.innerHTML += /*html*/ `<rect x="${-halfWidth}" y="0" width="${width}" height="225" fill="#f5f5f5" stroke="black" stroke-width="1"></rect>`;

    // Legend title
    g.innerHTML += /*html*/ `<text x="${-halfWidth + 6}" y="15" text-anchor="start" font-size="14px" font-weight="bold">Legend</text>`;

    // divider
    g.innerHTML += /*html*/ `<line x1="${-halfWidth}" x2="${width}" y1="21.5" y2="21.5" stroke="black" stroke-width="1"></line>`;

    // Gradient bar
    const gradient = createGradient("svgGradient", [25, 50, 75]);
    chart.select("defs").node().appendChild(gradient);
    g.innerHTML += /*html*/ `<rect x="-270" y="63" height="10" width="520" fill="url(#svgGradient)"></rect>`;

    // 95%/90% message
    g.innerHTML += /*html*/ `
    <rect x="-271" y="63" height="16" width="2" fill="black"></rect>
    <text x="-271" y="40" text-anchor="middle" font-size="12px"><tspan text-decoration="underline">95%</tspan><tspan> or 90%</tspan></text>
    <text x="-271" y="55" text-anchor="middle" font-size="12px">Exceedance</text>
    <text x="-271" y="90" text-anchor="middle" font-size="9.5px">There is a 95%/90% chance that</text>
    <text x="-271" y="101" text-anchor="middle" font-size="9.5px">flows will exceed this volume</text>
    `;

    // 70% message
    g.innerHTML += /*html*/ `
    <rect x="-141" y="63" height="16" width="2" fill="black"></rect>
    <text x="-141" y="40" text-anchor="middle" font-size="12px">70%</text>
    <text x="-141" y="55" text-anchor="middle" font-size="12px">Exceedance</text>
    <text x="-141" y="90" text-anchor="middle" font-size="9.5px">There is a 70% chance that</text>
    <text x="-141" y="101" text-anchor="middle" font-size="9.5px">flows will exceed this volume</text>
    `;

    // 50% message
    g.innerHTML += /*html*/ `
    <rect x="-11" y="63" height="16" width="2" fill="black"></rect>
    <text x="-11" y="40" text-anchor="middle" font-size="12px" font-weight="bold">50%</text>
    <text x="-11" y="55" text-anchor="middle" font-size="12px" font-weight="bold">Exceedance</text>
    <text x="-11" y="90" text-anchor="middle" font-size="9.5px">There is a 50% chance that</text>
    <text x="-11" y="101" text-anchor="middle" font-size="9.5px">flows will exceed this volume</text>
    `;

    // 30% message
    g.innerHTML += /*html*/ `
    <rect x="119" y="63" height="16" width="2" fill="black"></rect>
    <text x="119" y="40" text-anchor="middle" font-size="12px">30%</text>
    <text x="119" y="55" text-anchor="middle" font-size="12px">Exceedance</text>
    <text x="119" y="90" text-anchor="middle" font-size="9.5px">There is a 30% chance that</text>
    <text x="119" y="101" text-anchor="middle" font-size="9.5px">flows will exceed this volume</text>
    `;

    // 10% message
    g.innerHTML += /*html*/ `
    <rect x="249" y="63" height="16" width="2" fill="black"></rect>
    <text x="249" y="40" text-anchor="middle" font-size="12px"><tspan >10% or </tspan><tspan text-decoration="underline"> 5%</tspan ></text>
    <text x="249" y="55" text-anchor="middle" font-size="12px">Exceedance</text>
    <text x="249" y="90" text-anchor="middle" font-size="9.5px">There is a 10%/5% chance that</text>
    <text x="249" y="101" text-anchor="middle" font-size="9.5px">flows will exceed this volume</text>
    `;

    // obs/stats box
    g.innerHTML += /*html*/ `
    <rect x="-330" y="114" width="660" height="81" fill="none" stroke="#444"></rect>
    <text x="-10" y="130" text-anchor="middle" font-size="12px">When selected, the following historic streamflow values and statistics will be shown.</text>

    <rect x="-247.5" y="137" height="22" width="4" fill="red" fill-opacity="0.3" rx="4" ry="4" stroke="red" stroke-width="2"></rect>
    <text x="-247.5" y="171" text-anchor="middle" font-size="12px" font-style="italic" fill="red">Period of Record Minimum</text>
    <text x="-247.5" y="185" text-anchor="middle" font-size="12px" font-style="italic" fill="red">Streamflow KAF (Year)</text>

    <rect x="-87.5" y="137" height="22" width="4" fill="green" fill-opacity="0.3" rx="4" ry="4" stroke="green" stroke-width="2"></rect>
    <text x="-87.5" y="171" text-anchor="middle" font-size="12px" font-style="italic" fill="green">1991-2020 Normal</text>
    <text x="-87.5" y="185" text-anchor="middle" font-size="12px" font-style="italic" fill="green">Streamflow KAF </text>

    <rect x="72.5" y="137" height="22" width="4" fill="black" fill-opacity="0.3" rx="4" ry="4" stroke="black" stroke-width="2"></rect>
    <text x="72.5" y="171" text-anchor="middle" font-size="12px" font-style="italic" fill="black">Observed</text>
    <text x="72.5" y="185" text-anchor="middle" font-size="12px" font-style="italic" fill="black">Streamflow KAF </text>

    <rect x="232.5" y="137" height="22" width="4" fill="blue" fill-opacity="0.3" rx="4" ry="4" stroke="blue" stroke-width="2"></rect>
    <text x="232.5" y="171" text-anchor="middle" font-size="12px" font-style="italic" fill="blue">Period of Record Maximum</text>
    <text x="232.5" y="185" text-anchor="middle" font-size="12px" font-style="italic" fill="blue">Streamflow KAF (Year) </text>
    `;

    // footer
    g.innerHTML += /*html*/ `
    <text x="0" y="214" text-anchor="middle" font-size="11px">Some forecasts may be for volumes that are regulated or influenced by diversions and water management. </text>
    `;

    chart.node().appendChild(g);
}
